import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronRight, X } from "lucide-react";
import { api } from "@/lib/api";
import { inputCls } from "./ProjectsPanel";

const STATUSES = ["New", "Under Review", "Approved", "Declined", "Access Granted"];
const STATUS_STYLE = {
  New: "text-gold border-gold/40 bg-gold/5",
  "Under Review": "text-sky-300 border-sky-400/40 bg-sky-400/5",
  Approved: "text-emerald-300 border-emerald-400/40 bg-emerald-400/5",
  Declined: "text-red-300 border-red-400/40 bg-red-400/5",
  "Access Granted": "text-emerald-400 border-emerald-500/50 bg-emerald-500/10",
};
const ORG_TYPES = ["Investor", "Strategic Partner", "Technology Partner", "Financial Institution", "Government / Institution", "Advisor", "Other"];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function RequestsPanel({ requests, reload }) {
  const [filters, setFilters] = useState({ resource: "", org_type: "", country: "", status: "", from: "" });
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [days, setDays] = useState(7);

  const counts = useMemo(() => {
    const c = { Total: requests.length, New: 0, "Under Review": 0, Approved: 0, "Access Granted": 0 };
    requests.forEach((r) => { const s = r.status || "New"; if (c[s] !== undefined) c[s] += 1; });
    return c;
  }, [requests]);

  const resourceOptions = useMemo(() => [...new Set(requests.map((r) => r.resource_title || r.document_title).filter(Boolean))], [requests]);

  const filtered = requests.filter((r) => {
    if (filters.status && (r.status || "New") !== filters.status) return false;
    if (filters.resource && (r.resource_title || r.document_title) !== filters.resource) return false;
    if (filters.org_type && r.org_type !== filters.org_type) return false;
    if (filters.country && !(r.country || "").toLowerCase().includes(filters.country.toLowerCase())) return false;
    if (filters.from && (r.created_at || "") < new Date(filters.from).toISOString()) return false;
    return true;
  });

  const refreshSelected = async (id) => {
    const { data } = await api.get("/admin/access-requests");
    setSelected(data.find((r) => r.id === id) || null);
  };

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      await reload();
      await refreshSelected(selected.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const setStatus = (s) => act(() => api.put(`/admin/access-requests/${selected.id}/status`, { status: s }), `Status updated to ${s}`);
  const grant = () => act(() => api.post(`/admin/access-requests/${selected.id}/grant`, { expires_days: Number(days) || 7 }), "Secure access link issued and emailed to the applicant");
  const respond = () => act(() => api.post(`/admin/access-requests/${selected.id}/respond`), "Status response emailed to the applicant");
  const addNote = () => {
    if (!note.trim()) return;
    act(() => api.post(`/admin/access-requests/${selected.id}/notes`, { note }), "Internal note added");
    setNote("");
  };

  return (
    <div data-testid="requests-panel">
      <h2 className="font-serif text-2xl text-ivory mb-6">Investor Requests</h2>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
        {["Total", "New", "Under Review", "Approved", "Access Granted"].map((k) => (
          <button
            key={k}
            data-testid={`requests-summary-${k.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setFilters({ ...filters, status: k === "Total" ? "" : k })}
            className={`border p-4 text-left transition-colors ${filters.status === k ? "border-gold/60 bg-gold/5" : "border-white/10 bg-ink hover:border-gold/30"}`}
          >
            <span className="block font-serif text-3xl text-ivory">{counts[k]}</span>
            <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-white/45">{k}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <select data-testid="filter-resource" className={inputCls} value={filters.resource} onChange={(e) => setFilters({ ...filters, resource: e.target.value })}>
          <option value="">All Resources</option>
          {resourceOptions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select data-testid="filter-org-type" className={inputCls} value={filters.org_type} onChange={(e) => setFilters({ ...filters, org_type: e.target.value })}>
          <option value="">All Org Types</option>
          {ORG_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <input data-testid="filter-country" className={inputCls} placeholder="Country" value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} />
        <select data-testid="filter-status" className={inputCls} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All Statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input data-testid="filter-date" type="date" className={inputCls} value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
      </div>

      {filtered.length === 0 && <p className="text-sm text-white/30">No requests match the current filters.</p>}
      <div className="divide-y divide-white/10 border border-white/10">
        {filtered.map((r) => (
          <button
            key={r.id}
            data-testid={`request-row-${r.id}`}
            onClick={() => setSelected(r)}
            className="w-full flex items-center gap-4 px-5 py-4 bg-ink hover:bg-surface/70 transition-colors text-left"
          >
            <span className="font-mono text-[10px] tracking-wider text-gold shrink-0 w-24">{r.request_code || "—"}</span>
            <div className="flex-1 min-w-0">
              <p className="text-ivory font-medium truncate">{r.name} <span className="text-white/40 text-sm font-normal">{r.company && `· ${r.company}`}</span></p>
              <p className="text-xs text-white/40 truncate">{r.resource_title || r.document_title} {r.org_type && `· ${r.org_type}`} {r.country && `· ${r.country}`}</p>
            </div>
            <span className="hidden sm:block font-mono text-[10px] text-white/35 shrink-0">{fmt(r.created_at)}</span>
            <span className={`shrink-0 border px-2.5 py-1 font-mono text-[9px] tracking-[0.15em] uppercase ${STATUS_STYLE[r.status || "New"]}`}>{r.status || "New"}</span>
            <ChevronRight size={14} className="text-white/30 shrink-0" />
          </button>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-[70] bg-ink/80 backdrop-blur-sm flex items-start lg:items-center justify-center p-4 overflow-y-auto" onClick={() => setSelected(null)}>
          <div data-testid="request-detail" className="bg-surface border border-white/10 w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <span className="font-mono text-[10px] tracking-[0.25em] text-gold">{selected.request_code}</span>
                <h3 className="font-serif text-2xl text-ivory">{selected.name}</h3>
              </div>
              <button data-testid="request-close-btn" onClick={() => setSelected(null)} className="text-white/40 hover:text-gold"><X size={18} /></button>
            </div>

            <div className="p-6 grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 mb-4">Applicant</h4>
                <dl className="space-y-2.5 text-sm">
                  {[["Organization", selected.company], ["Business Email", selected.email], ["Country", selected.country],
                    ["Role / Position", selected.role], ["Organization Type", selected.org_type],
                    ["Requested Resource", selected.resource_title || selected.document_title],
                    ["Submitted", fmt(selected.created_at)]].map(([k, v]) => (
                    <div key={k} className="flex gap-3"><dt className="w-40 shrink-0 text-white/35">{k}</dt><dd className="text-ivory">{v || "—"}</dd></div>
                  ))}
                </dl>
                <h4 className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 mt-6 mb-2">Reason for Access</h4>
                <p className="text-sm text-white/60 leading-relaxed">{selected.reason || "—"}</p>

                <h4 className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 mt-6 mb-2">Internal Notes</h4>
                {(selected.notes || []).length === 0 && <p className="text-sm text-white/30">No notes yet.</p>}
                {(selected.notes || []).map((n, i) => (
                  <p key={i} className="text-sm text-white/60 border-l border-gold/40 pl-3 mb-2">{n.note} <span className="text-white/30 text-xs">— {fmt(n.at)}</span></p>
                ))}
                <div className="flex gap-2 mt-3">
                  <input data-testid="request-note-input" className={inputCls} placeholder="Add internal note" value={note} onChange={(e) => setNote(e.target.value)} />
                  <button data-testid="request-add-note-btn" onClick={addNote} className="shrink-0 px-4 border border-white/15 font-mono text-[10px] tracking-[0.15em] uppercase text-white/60 hover:border-gold/50 hover:text-gold">Add</button>
                </div>
              </div>

              <div>
                <h4 className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 mb-4">Status & Actions</h4>
                <span className={`inline-block border px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] uppercase ${STATUS_STYLE[selected.status || "New"]}`} data-testid="request-current-status">
                  {selected.status || "New"}
                </span>
                <div className="flex flex-wrap gap-2 mt-5">
                  <button data-testid="request-approve-btn" onClick={() => setStatus("Approved")} className="px-4 py-2.5 border border-emerald-500/40 text-emerald-300 font-mono text-[10px] tracking-[0.15em] uppercase hover:bg-emerald-500/10">Approve</button>
                  <button data-testid="request-decline-btn" onClick={() => setStatus("Declined")} className="px-4 py-2.5 border border-red-400/40 text-red-300 font-mono text-[10px] tracking-[0.15em] uppercase hover:bg-red-400/10">Decline</button>
                  <button data-testid="request-review-btn" onClick={() => setStatus("Under Review")} className="px-4 py-2.5 border border-sky-400/40 text-sky-300 font-mono text-[10px] tracking-[0.15em] uppercase hover:bg-sky-400/10">Under Review</button>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button data-testid="request-grant-btn" onClick={grant} className="px-4 py-2.5 bg-gold text-ink font-mono text-[10px] tracking-[0.15em] uppercase hover:bg-gold-light">Grant Access</button>
                  <input data-testid="request-grant-days" type="number" min={1} max={30} value={days} onChange={(e) => setDays(e.target.value)} className={`${inputCls} w-20`} />
                  <span className="text-xs text-white/35">days</span>
                </div>
                <p className="text-xs text-white/30 mt-2">Granting issues a time-limited secure link and emails it to the applicant. Documents are never attached to email.</p>
                <button data-testid="request-respond-btn" onClick={respond} className="mt-4 px-4 py-2.5 border border-white/20 text-white/70 font-mono text-[10px] tracking-[0.15em] uppercase hover:border-gold/50 hover:text-gold">Send Response Email</button>

                <RequestGrants requestId={selected.id} />

                <h4 className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 mt-8 mb-3">History</h4>
                <div className="space-y-2">
                  {(selected.history || []).slice().reverse().map((h, i) => (
                    <div key={i} className="text-xs text-white/50 border-l border-white/15 pl-3">
                      <span className="text-ivory">{h.action}</span> <span className="text-white/30">· {fmt(h.at)} · {h.by}</span>
                      {h.note && <p className="text-white/40 mt-0.5">{h.note}</p>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function RequestGrants({ requestId }) {
  const [grants, setGrants] = useState([]);

  const load = () => api.get(`/admin/access-requests/${requestId}/grants`).then((r) => setGrants(r.data)).catch(() => {});
  useEffect(() => { load(); }, [requestId]);

  const revoke = async (token) => {
    try {
      await api.delete(`/admin/grants/${token}`);
      toast.success("Access revoked — the link no longer works");
      load();
    } catch {
      toast.error("Revoke failed");
    }
  };

  if (!grants.length) return null;

  return (
    <div className="mt-8" data-testid="request-grants">
      <h4 className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 mb-3">Issued Access Links</h4>
      {grants.map((g) => (
        <div key={g.token} data-testid={`grant-${g.token.slice(0, 8)}`} className="border border-white/10 bg-ink p-4 mb-2">
          <p className="font-mono text-[10px] text-gold break-all">{g.link}</p>
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-white/40">
              {g.resource_title} · expires {fmt(g.expires_at)}{g.expired && <span className="text-red-300"> · expired</span>}
            </p>
            <button data-testid={`grant-revoke-${g.token.slice(0, 8)}`} onClick={() => revoke(g.token)}
              className="font-mono text-[9px] tracking-[0.15em] uppercase text-red-300/80 hover:text-red-300 border border-red-400/30 px-3 py-1.5">
              Revoke Access
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
